import { definedMap, hasOwnProperty } from "collection-utils";
import last from "lodash/last";
import {
    combineEditedColumnsAndTables,
    componentErrorAndLinkForReference,
    emptyEditedColumnsAndTables,
    getPropertyDescription,
    makeStringyColumnPropertyDescriptor,
    RequiredKind,
} from ".";
import { AppKind } from "../../common/components/app-kind";
import type { BasePrimitiveValue } from "../../common/components/computation-types";
import type {
    ActionDescription,
    ArrayFilter,
    ColumnAssignment,
    ColumnTypeKind,
    Description,
    FormScreenDescription,
    InputOutputTables,
    PropertyDescription,
    PropertySource,
    SourceColumn,
    TableAndColumn,
    TableColumn,
    TableGlideType,
} from "../../common/description";
import {
    getBackendCompatiblePrimitiveColumns,
    getInputOrOutputTable,
    getPrimitiveColumns,
    getPrimitiveNonHiddenColumns,
    getPrimitiveOrPrimitiveArrayNonHiddenColumns,
    getStringTypeOrStringTypeArrayColumns,
    getTableColumn,
    getTableColumnDisplayName,
    ImageKind,
    isColumnNonHidden,
    isColumnWriteable,
    isDateTimeTypeKind,
    isMultiRelationType,
    isNumberTypeKind,
    isSingleRelationType,
    MutatingScreenKind,
    PropertyKind,
    sheetNameForTable,
} from "../../common/description";
import { isFavoritedColumnName } from "../../common/google-sheets";
import { assert, checkString, defined, removeArrayItem, replaceArrayItem } from "../../common/Support";
import type {
    ActionPropertyDescriptorCase,
    AppDescriptionContext,
    ArrayPropertyDescriptorCase,
    ColumnPropertyDescriptorCase,
    EditedColumnsAndTables,
    EnumPropertyCase,
    EnumPropertyDescriptorCase,
    GetAllowedColumnsFunction,
    MultiCasePropertyDescriptor,
    NumberPropertyDescriptorCase,
    PropertyDescriptor,
    PropertyDescriptorCase,
    PropertyLabel,
    PropertyTableGetter,
    SchemaInspector,
    SpecialValuePropertyDescriptorCase,
    StringPropertyDescriptorCase,
    StringyColumnPropertyFlag,
    TablePropertyDescriptorCase,
} from "../../component-utils";
import { imageProperties, isImageType, titleProperties } from "../../component-utils/description-utils";
import type {
    ColumnPropertyDescriptorFlags,
    TextPropertyDescriptorFlags,
} from "../../component-utils/descriptor-utils";
import {
    makeColumnCase,
    makeColumnOrLiteralPropertyDescriptor,
    makeImagePropertyDescriptor,
    makeTextPropertyDescriptor,
} from "../../component-utils/descriptor-utils";
import {
    doFilterColumnsExist,
    getColumnAssignments,
    getColumnNameAndGroup,
    getPropertyAndDescriptorFromDescription,
    getPropertyDescriptorCaseForDescription,
    getPropertyDescriptorCaseForKind,
} from "../description-utils";
import { resolveSourceColumn } from "../source-column";
import { getUserProfileTableInfo } from "../user-profile-info";
import { handlerForPropertyKind } from "./description-handlers";
import { ColumnPropertyFlag, ColumnPropertyHandler, SwitchPropertyHandler } from "./property-handlers";
import type { SuperPropertySection } from "./property-sections";
import { PropertySection } from "./property-sections";
import {
    getArrayProperty,
    getColumnProperty,
    getSourceColumnProperty,
    getTableProperty,
    makeEnumProperty,
} from "./property-utils";
export * from "../../component-utils/descriptor-utils";

function getColumnPropertyDescription<T extends Description>(
    pd: PropertyDescriptor,
    desc: T
): { sourceColumn: SourceColumn; columnDescr: ColumnPropertyDescriptorCase } | undefined {
    const propertyDesc = getPropertyDescription(desc, pd);
    if (propertyDesc === undefined) return undefined;
    const sourceColumn = getSourceColumnProperty(propertyDesc);
    if (sourceColumn === undefined) return undefined;
    const columnDescr = getPropertyDescriptorCaseForDescription(propertyDesc, pd) as ColumnPropertyDescriptorCase;
    if (columnDescr === undefined) return undefined;

    return { sourceColumn, columnDescr };
}

interface ColumnWithDescriptorCase extends TableAndColumn {
    readonly columnDescr: ColumnPropertyDescriptorCase;
}

export function getColumnForProperty(
    pd: PropertyDescriptor,
    desc: Description,
    tables: InputOutputTables,
    ccc: AppDescriptionContext
): ColumnWithDescriptorCase | undefined {
    const propertyDesc = getColumnPropertyDescription(pd, desc);
    if (propertyDesc === undefined) return undefined;
    const { columnDescr, sourceColumn } = propertyDesc;

    const table = getInputOrOutputTable(tables, columnDescr.isEditedInApp === true);
    let propertyTable: TableGlideType | undefined;
    if (columnDescr.getIndirectTable === undefined) {
        propertyTable = table;
    } else {
        if (ccc === undefined) return undefined;
        propertyTable = columnDescr.getIndirectTable(tables, desc, ccc);
    }

    const resolved = definedMap(resolveSourceColumn(ccc, sourceColumn, propertyTable, undefined), last);
    if (resolved === undefined) return undefined;

    return { ...resolved, columnDescr };
}

// This will only return columns used from the context, not, for example,
// columns used from the user profile row.
export function getColumnsUsedInDescription<T extends Description>(
    propertyDescriptors: ReadonlyArray<PropertyDescriptor>,
    desc: Description,
    tables: InputOutputTables,
    ccc: AppDescriptionContext,
    predicate?: (column: TableColumn, columnDescr: ColumnPropertyDescriptorCase) => boolean
): ReadonlyArray<TableColumn> {
    const columns: TableColumn[] = [];
    for (const pd of propertyDescriptors) {
        const column = getColumnForProperty(pd, desc, tables, ccc);
        if (column !== undefined) {
            if (column.table !== tables.input && column.table !== tables.output) continue;
            if (predicate !== undefined && !predicate(column.column, column.columnDescr)) continue;
            columns.push(column.column);
            continue;
        }

        const propertyDesc = getPropertyDescription(desc, pd);
        if (propertyDesc === undefined) continue;

        const descrCase = getPropertyDescriptorCaseForDescription(propertyDesc, pd);

        if (descrCase?.kind === PropertyKind.Array) {
            const arr = getArrayProperty<T>(propertyDesc);
            if (arr === undefined) continue;
            for (const item of arr) {
                columns.push(
                    ...getColumnsUsedInDescription(
                        (descrCase as ArrayPropertyDescriptorCase).properties,
                        item,
                        tables,
                        ccc,
                        predicate
                    )
                );
            }
        }
    }
    return columns;
}

export function getInputOrOutputTableForProperty(
    tables: InputOutputTables,
    pdc: ColumnPropertyDescriptorCase
): TableGlideType {
    return getInputOrOutputTable(tables, pdc.isEditedInApp === true);
}

export function rewriteFilter(filter: ArrayFilter, table: TableGlideType): ArrayFilter | undefined {
    if (doFilterColumnsExist(filter, table) === true) {
        return filter;
    }
    return undefined;
}

export function getDefaultCaptionColumnDescriptor(
    propertyDescriptors: ReadonlyArray<PropertyDescriptor>
): { descr: PropertyDescriptor; pdc: ColumnPropertyDescriptorCase } | undefined {
    for (const descr of propertyDescriptors) {
        const pdc = getPropertyDescriptorCaseForKind(PropertyKind.Column, descr);

        if (pdc?.kind !== PropertyKind.Column) continue;
        const cpdc = pdc as ColumnPropertyDescriptorCase;
        if (cpdc.isDefaultCaption !== true) continue;

        return { descr, pdc: cpdc };
    }
    return undefined;
}

export function getDefaultCaption(
    propertyDescriptors: ReadonlyArray<PropertyDescriptor>,
    desc: Description,
    tables: InputOutputTables | undefined,
    schema: SchemaInspector
): string | undefined {
    const defaultCaptionDescriptor = getDefaultCaptionColumnDescriptor(propertyDescriptors);
    if (defaultCaptionDescriptor === undefined) return undefined;
    const { descr, pdc } = defaultCaptionDescriptor;

    const pd = getPropertyDescription(desc, descr);
    if (pd?.kind === PropertyKind.Column && pdc.kind === PropertyKind.Column) {
        assert(pdc.kind === PropertyKind.Column);
        if (tables === undefined) return undefined;
        const sourceColumn = getSourceColumnProperty(pd);
        if (sourceColumn === undefined) return undefined;
        const columnName = checkString(sourceColumn.name);
        const table = getInputOrOutputTable(tables, pdc.isEditedInApp === true);
        const column = getTableColumn(table, columnName);
        if (column === undefined) return columnName;
        return getColumnNameAndGroup(column)[1];
    } else if (pd?.kind === PropertyKind.Table) {
        const tableName = getTableProperty(pd);
        if (tableName === undefined) return undefined;
        const table = schema.findTable(tableName);
        if (table === undefined) return undefined;
        return sheetNameForTable(table);
    } else {
        return undefined;
    }
}

const imageKindCases: ReadonlyArray<EnumPropertyCase<ImageKind>> = [
    { value: ImageKind.URL, label: "URL" },
    { value: ImageKind.MapFromAddress, label: "Map from address" },
];

export function makeTitlePropertyDescriptor(
    getPropertyTable: PropertyTableGetter | undefined,
    required: boolean,
    isEditedInApp: boolean
): PropertyDescriptor {
    return makeStringyColumnPropertyDescriptor(
        "titleProperty",
        "Title",
        [
            required ? "required" : "optional",
            "editable",
            "searchable",
            ...(isEditedInApp ? ["edited-in-app" as const] : []),
        ],
        PropertySection.Data,
        getPropertyTable,
        titleProperties
    );
}

export function makeSubtitlePropertyDescriptor(getPropertyTable: PropertyTableGetter | undefined): PropertyDescriptor {
    return makeStringyColumnPropertyDescriptor(
        "subtitleProperty",
        "Details",
        ["editable", "searchable"],
        PropertySection.Data,
        getPropertyTable,
        titleProperties,
        ["titleProperty"]
    );
}

export function makeSummaryImagePropertyDescriptor(
    getPropertyTable: PropertyTableGetter | undefined,
    imageRequired: boolean,
    isEditedInApp: boolean,
    withEmoji: boolean
): PropertyDescriptor {
    return {
        kind: PropertyKind.Column,
        property: { name: "imageURLProperty" },
        label: "Image",
        required: imageRequired,
        editable: true,
        searchable: false,
        isEditedInApp,
        getIndirectTable: getPropertyTable,
        getAllowedColumns: getStringTypeOrStringTypeArrayColumns,
        preferredNames: imageProperties,
        preferredType: t => isImageType(t, withEmoji, true),
        section: PropertySection.Data,
    };
}

export function makeSummaryImageKindPropertyDescriptor(imageKind: ImageKind | undefined): PropertyDescriptor {
    if (imageKind === undefined) {
        return {
            kind: PropertyKind.Enum,
            property: { name: "imageKind" },
            label: "Image is",
            menuLabel: "Image source",
            cases: imageKindCases,
            defaultCaseValue: ImageKind.URL,
            section: PropertySection.Design,
            visual: "dropdown",
            isSearchable: false,
        };
    } else {
        return {
            kind: PropertyKind.Constant,
            property: { name: "imageKind" },
            // This isn't used
            label: "Image is",
            value: imageKind,
            doNotRewrite: true,
            section: PropertySection.Hidden,
        };
    }
}

export function makeSummaryPropertyDescriptors(
    titleRequired: boolean,
    includeDescription: boolean,
    // if `imageRequired` is `undefined`, don't include an image
    imageRequired: boolean | undefined,
    imageKind: ImageKind | undefined,
    getPropertyTable: PropertyTableGetter | undefined,
    withEmoji: boolean,
    imageFirst: boolean,
    additionalDescriptorsBeforeImage: readonly PropertyDescriptor[] = []
): readonly PropertyDescriptor[] {
    const descrs: PropertyDescriptor[] = [makeTitlePropertyDescriptor(getPropertyTable, titleRequired, false)];

    if (includeDescription) {
        descrs.push(makeSubtitlePropertyDescriptor(getPropertyTable));
    }
    if (imageFirst) {
        descrs.unshift(...additionalDescriptorsBeforeImage);
    } else {
        descrs.push(...additionalDescriptorsBeforeImage);
    }

    if (imageRequired !== undefined) {
        const imageDescriptor = makeSummaryImagePropertyDescriptor(getPropertyTable, imageRequired, false, withEmoji);
        const imageKindDescriptor = makeSummaryImageKindPropertyDescriptor(imageKind);

        if (imageFirst) {
            descrs.unshift(imageDescriptor);
        } else {
            descrs.push(imageDescriptor);
        }

        descrs.push(imageKindDescriptor);
    }

    return descrs;
}

export function makeCaptionPropertyDescriptor(
    getPropertyTable: PropertyTableGetter | undefined,
    emptyByDefault: boolean
): PropertyDescriptor {
    const flags: StringyColumnPropertyFlag[] = ["editable", "searchable"];
    if (emptyByDefault) {
        flags.push("empty-by-default");
    }

    return makeStringyColumnPropertyDescriptor(
        "captionProperty",
        "Caption",
        flags,
        PropertySection.Data,
        getPropertyTable,
        ["caption", ...titleProperties],
        ["titleProperty", "subtitleProperty"]
    );
}

const primaryKeyPropertyName = "primaryKeyProperty";

export function makePrimaryKeyPropertyHandler(
    ccc: AppDescriptionContext,
    table: TableGlideType | undefined,
    isLegacy: boolean = true,
    // This is for ##nonEditableColumnProperty
    showAsDisabledIfRowIDPresent: boolean = false
): readonly ColumnPropertyHandler[] {
    const flags = new Set([ColumnPropertyFlag.Editable, ColumnPropertyFlag.EmptyByDefault]);
    let getAllowedColumns: GetAllowedColumnsFunction = getBackendCompatiblePrimitiveColumns;

    const rowIDColumn = table?.rowIDColumn !== undefined ? getTableColumn(table, table.rowIDColumn) : undefined;
    if (rowIDColumn !== undefined) {
        if (showAsDisabledIfRowIDPresent) {
            flags.delete(ColumnPropertyFlag.Editable);
            getAllowedColumns = () => [rowIDColumn];
        } else {
            return [];
        }
    }

    if (isLegacy && !ccc.userFeatures.primaryKeyProperties) {
        flags.add(ColumnPropertyFlag.Legacy);
    }

    return [
        new ColumnPropertyHandler(
            primaryKeyPropertyName,
            "Key",
            Array.from(flags),
            undefined,
            ["key", "id", "primary key", "identifier"],
            getAllowedColumns,
            "string",
            PropertySection.Data
        ),
    ];
}

export function getPrimaryKeyProperty(desc: Description): string | undefined {
    if (!hasOwnProperty(desc, primaryKeyPropertyName)) return undefined;
    return getColumnProperty(desc[primaryKeyPropertyName] as PropertyDescription);
}

export const useFallbackInitialsPropertyHandler = new SwitchPropertyHandler(
    {
        useFallbackInitials: false,
    },
    "Generate image when data is not available",
    PropertySection.Options
);

export type PropertyDescriptorsWithColumns = [readonly PropertyDescriptor[], readonly TableColumn[]];

export function makePropertyDescriptorsForColumns<TDesc extends Description>(
    tables: InputOutputTables,
    appKind: AppKind,
    getColumns: (desc: Description) => readonly ColumnAssignment[],
    makeUpdate: (columns: readonly ColumnAssignment[]) => Partial<TDesc>,
    {
        withActionSource,
        withClearColumn,
        allowUserSpecificColumns,
        allowCustomAndUserProfile,
        emptyByDefault,
        propertySection = PropertySection.Data,
        excludeOutputColumns,
    }: {
        withActionSource: boolean;
        withClearColumn: boolean;
        allowUserSpecificColumns: boolean;
        allowCustomAndUserProfile: boolean;
        emptyByDefault: boolean;
        propertySection?: SuperPropertySection;
        excludeOutputColumns?: ReadonlySet<string>;
    }
): PropertyDescriptorsWithColumns {
    const { input: inputTable, output: destTable } = tables;

    const writeableColumns =
        definedMap(destTable, t =>
            t.columns.filter(
                c =>
                    isColumnWriteable(c, destTable, allowUserSpecificColumns, true) &&
                    excludeOutputColumns?.has(c.name) !== true &&
                    // ##excludeIsFavoritedColumn:
                    // TODO: This should only happen for Pages, and for Apps if the favorite
                    // column isn't set in that table.
                    (appKind === AppKind.App || c.name !== isFavoritedColumnName)
            )
        ) ?? [];
    const withColumnCase = getPrimitiveNonHiddenColumns(inputTable).length > 0;
    const descrs = writeableColumns.map(c => {
        const cases: PropertyDescriptorCase[] = [];

        if (withColumnCase) {
            cases.push({
                kind: PropertyKind.Column,
                required: false,
                editable: true,
                searchable: false,
                emptyByDefault,
                allowUserProfileColumns: allowCustomAndUserProfile,
                getAllowedColumns: getPrimitiveNonHiddenColumns,
                preferredNames: [c.name],
                preferredType: c.type.kind,
            } as ColumnPropertyDescriptorCase);
        }
        cases.push({
            kind: PropertyKind.SpecialValue,
            withActionSource,
            withClearColumn,
        } as SpecialValuePropertyDescriptorCase);
        if (allowCustomAndUserProfile) {
            cases.push({
                kind: PropertyKind.String,
                required: false,
                placeholder: "Custom",
            } as StringPropertyDescriptorCase);
        }

        function findAssignment(d: Description): [ColumnAssignment, number] | undefined {
            const columns = getColumns(d);
            const index = columns.findIndex(a => a.destColumn === c.name);
            if (index < 0) return undefined;
            return [columns[index], index];
        }

        return {
            label: getTableColumnDisplayName(c),
            property: {
                id: c.name,
                get: d => definedMap(findAssignment(d), ([a]) => a.value),
                update: (d, v) => {
                    let columns = getColumns(d);
                    const index = definedMap(findAssignment(d), ([, i]) => i);
                    if (v !== undefined) {
                        const newAssignment = { destColumn: c.name, value: v };
                        if (index !== undefined) {
                            columns = replaceArrayItem(columns, index, newAssignment);
                        } else {
                            columns = [...columns, newAssignment];
                        }
                    } else if (index !== undefined) {
                        columns = removeArrayItem(columns, index);
                    }
                    return makeUpdate(columns);
                },
            } as PropertySource,
            cases,
            section: propertySection,
            writesToColumn: { table: destTable, column: c },
        } as MultiCasePropertyDescriptor;
    });
    return [descrs, writeableColumns];
}

export function makePropertyDescriptorsForFormScreen(
    tables: InputOutputTables,
    appKind: AppKind,
    withActionSource: boolean
): readonly PropertyDescriptor[] {
    return makePropertyDescriptorsForColumns<FormScreenDescription>(
        tables,
        appKind,
        getColumnAssignments,
        cs => ({
            columnAssignments: cs,
        }),
        {
            withActionSource: withActionSource,
            withClearColumn: false,
            allowUserSpecificColumns: false,
            allowCustomAndUserProfile: false,
            emptyByDefault: false,
        }
    )[0];
}

export const screenTitlePropertyDescriptor: PropertyDescriptor = {
    property: { name: "title" },
    label: "Title",
    section: PropertySection.Title,
    cases: [
        {
            kind: PropertyKind.Column,
            required: false,
            editable: true,
            searchable: false,
            getAllowedColumns: getPrimitiveColumns,
            preferredNames: ["title", "name"],
            preferredType: "string",
        } as ColumnPropertyDescriptorCase,
        {
            kind: PropertyKind.String,
            required: false,
            placeholder: "Screen title",
        } as StringPropertyDescriptorCase,
    ],
};

export function makeTextPropertyDescriptorWithSpecialValue(
    property: string | PropertySource,
    label: PropertyLabel,
    placeholder: string,
    required: boolean,
    mutatingScreenKind: MutatingScreenKind | undefined,
    flags: TextPropertyDescriptorFlags
): PropertyDescriptor {
    return makeTextPropertyDescriptor(property, label, placeholder, required, mutatingScreenKind, flags, [
        {
            kind: PropertyKind.SpecialValue,
            withActionSource: false,
            withClearColumn: false,
        },
    ]);
}

export function doesMutatingScreenKindSupportDefaultValues(
    mutatingScreenKind: MutatingScreenKind | undefined
): boolean {
    return mutatingScreenKind === MutatingScreenKind.AddScreen || mutatingScreenKind === MutatingScreenKind.FormScreen;
}

// ##makeDefaultValuePropertyDescriptor:
// This makes a property descriptor for default values, which are allowed in
// add and form screens.
export function makeDefaultValuePropertyDescriptor(
    ccc: AppDescriptionContext,
    mutatingScreenKind: MutatingScreenKind | undefined,
    defaultPreferredType: ColumnTypeKind,
    placeholder: string = "Enter the default value",
    withCustomCase: boolean = true,
    enumOptions?: readonly BasePrimitiveValue[]
): PropertyDescriptor | undefined {
    if (!doesMutatingScreenKindSupportDefaultValues(mutatingScreenKind)) return undefined;

    let columnCase: ColumnPropertyDescriptorCase | undefined;
    if (
        mutatingScreenKind === MutatingScreenKind.FormScreen ||
        definedMap(ccc.appDescription, getUserProfileTableInfo) !== undefined
    ) {
        columnCase = makeColumnCase(
            defaultPreferredType === "boolean",
            {
                searchable: false,
                applyFormat: false,
                emptyByDefault: true,
                needsExistingValue: true,
            },
            defaultPreferredType
        );
    }

    const otherCases: (
        | StringPropertyDescriptorCase
        | NumberPropertyDescriptorCase
        | EnumPropertyDescriptorCase<any>
    )[] = [];
    if (enumOptions !== undefined) {
        const enumCases: EnumPropertyCase<any>[] = enumOptions.map(o => ({ label: o.toString(), value: o }));
        if (columnCase === undefined) {
            enumCases.unshift({ label: "—", value: "" });
        }
        otherCases.push({
            kind: PropertyKind.Enum,
            menuLabel: "Default value",
            cases: enumCases,
            defaultCaseValue: enumCases[0]?.value,
            visual: "dropdown",
        });
    } else if (!withCustomCase) {
        // no custom case
    } else if (isNumberTypeKind(defaultPreferredType)) {
        otherCases.push({
            kind: PropertyKind.Number,
            required: RequiredKind.NotRequiredDefaultMissing,
            placeholder,
            defaultValue: 0,
        });
    } else if (defaultPreferredType === "boolean") {
        otherCases.push({
            kind: PropertyKind.Enum,
            menuLabel: "Default value",
            cases: [
                {
                    label: "On",
                    value: true,
                },
                {
                    label: "Off",
                    value: false,
                },
            ],
            defaultCaseValue: false,
            visual: "dropdown",
        });
    } else {
        if (isDateTimeTypeKind(defaultPreferredType)) {
            const enumCase: EnumPropertyDescriptorCase<"now"> = {
                kind: PropertyKind.Enum,
                menuLabel: "Default value",
                cases: [
                    {
                        label: defaultPreferredType === "date" ? "Today" : "Now",
                        value: "now",
                    },
                ],
                defaultCaseValue: undefined,
                visual: "dropdown",
            };
            otherCases.push(enumCase);
        }

        otherCases.push({
            kind: PropertyKind.String,
            required: false,
            isCaption: undefined,
            placeholder,
            isMultiLine: false,
            menuLabel: "Custom",
        });
    }

    return makeColumnOrLiteralPropertyDescriptor(
        "defaultValue",
        "Default value",
        mutatingScreenKind,
        PropertySection.Data,
        undefined,
        enumOptions !== undefined,
        columnCase,
        otherCases
    );
}

interface CaptionPropertyDescriptorFlags {
    readonly label?: string; // default: `"Title"`
    readonly placeholder?: string; // default: `"Enter title"`
    readonly propertySection?: SuperPropertySection; // default: `PropertySection.Design`
}

export function makeCaptionStringPropertyDescriptor(
    defaultCaption: string,
    required: boolean,
    mutatingScreenKind: MutatingScreenKind | undefined,
    flags?: CaptionPropertyDescriptorFlags,
    propertyNameOverride?: string
): PropertyDescriptor {
    return makeTextPropertyDescriptor(
        propertyNameOverride ?? "caption",
        flags?.label ?? "Title",
        flags?.placeholder ?? "Enter title",
        required,
        mutatingScreenKind,
        {
            isCaption: defaultCaption,
            preferredNames: ["caption", "title", "name", "description"],
            propertySection: flags?.propertySection ?? PropertySection.Design,
            searchable: false,
        }
    );
}

export function makeNumberPropertyDescriptor(
    propertyName: string,
    label: PropertyLabel,
    placeholder: string,
    required: RequiredKind,
    defaultValue: number,
    mutatingScreenKind: MutatingScreenKind | undefined,
    flags: ColumnPropertyDescriptorFlags
): PropertyDescriptor {
    const columnCase = makeColumnCase(required === RequiredKind.Required, flags, "number");
    const numberCase: NumberPropertyDescriptorCase = {
        kind: PropertyKind.Number,
        required,
        placeholder,
        defaultValue,
    };

    return defined(
        makeColumnOrLiteralPropertyDescriptor(
            propertyName,
            label,
            mutatingScreenKind,
            PropertySection.Data,
            undefined,
            false,
            columnCase,
            [numberCase]
        )
    );
}

export function makeTitleSubtitleImagePropertyDescriptors(
    mutatingScreenKind: MutatingScreenKind | undefined,
    titleRequired: boolean,
    withEmoji: boolean = false,
    getIndirectTable?: PropertyTableGetter | undefined
): readonly PropertyDescriptor[] {
    const titleDescriptor = makeTextPropertyDescriptor(
        "titleProperty",
        "Title",
        "Enter title",
        titleRequired,
        mutatingScreenKind,
        {
            preferredNames: titleProperties,
            columnFirst: true,
            getIndirectTable,
        }
    );
    const subtitleDescriptor = makeTextPropertyDescriptor(
        "subtitleProperty",
        "Details",
        "Enter details",
        false,
        mutatingScreenKind,
        {
            preferredNames: titleProperties,
            columnFirst: true,
            ignoreColumnsFromProperties: ["titleProperty"],
            getIndirectTable,
        }
    );
    const imageDescriptor = makeImagePropertyDescriptor(
        "imageURLProperty",
        "Image",
        "From URL or address",
        "Enter URL or address",
        false,
        false,
        false,
        withEmoji,
        true,
        getIndirectTable,
        mutatingScreenKind
    );
    return [titleDescriptor, subtitleDescriptor, imageDescriptor];
}

export const thisItemEnumPropertyDescriptorCase: EnumPropertyDescriptorCase<true> = {
    kind: PropertyKind.Enum,
    menuLabel: "This item",
    cases: [
        {
            value: true,
            label: "This item",
        },
    ],
    defaultCaseValue: true,
    visual: "dropdown",
};

export function makeTableOrRelationPropertyDescriptor(
    mutatingScreenKind: MutatingScreenKind | undefined,
    allowSingleRelations: boolean,
    // We always allow tables
    allowMultiRelations: boolean,
    sourceIsDefaultCaption: boolean,
    label: string,
    propertySection: SuperPropertySection,
    allowRewrite: boolean,
    propertySource: PropertySource
): PropertyDescriptor {
    assert(allowSingleRelations || allowMultiRelations);

    const withColumns = mutatingScreenKind !== MutatingScreenKind.AddScreen;
    const columnCase: PropertyDescriptorCase = {
        kind: PropertyKind.Column,
        required: true,
        editable: true,
        searchable: false,
        isDefaultCaption: sourceIsDefaultCaption,
        getAllowedColumns: t =>
            t.columns.filter(
                c =>
                    (allowMultiRelations && isMultiRelationType(c.type)) ||
                    (allowSingleRelations && isSingleRelationType(c.type))
            ),
        getEmptyCase: t => {
            const { errorMessage, linkURL } = componentErrorAndLinkForReference(t.input, allowMultiRelations);
            return {
                label: errorMessage,
                linkURL,
            };
        },
    } as ColumnPropertyDescriptorCase;
    const tableCase: TablePropertyDescriptorCase = {
        kind: PropertyKind.Table,
        required: true,
        isDefaultCaption: sourceIsDefaultCaption,
        getAllowedTables: (_desc, schema) => schema.tables,
    };
    const base = {
        property: propertySource as PropertySource,
        label,
        section: propertySection,
        doNotRewrite: !allowRewrite,
    };
    const cases: PropertyDescriptorCase[] = [];
    if (withColumns) {
        if (mutatingScreenKind === undefined && allowSingleRelations) {
            cases.push(thisItemEnumPropertyDescriptorCase);
        }
        cases.push(columnCase);
    }
    cases.push(tableCase);
    if (cases.length === 1) {
        const singleCase = cases[0];
        assert(singleCase.kind === PropertyKind.Column || singleCase.kind === PropertyKind.Table);
        return { ...base, ...(singleCase as ColumnPropertyDescriptorCase | TablePropertyDescriptorCase) };
    } else {
        return { ...base, cases };
    }
}

export function makeSingleRelationOrThisItemPropertyDescriptor(
    propertyName: string,
    label: string,
    section: SuperPropertySection,
    forWriting: boolean
): PropertyDescriptor {
    return {
        property: { name: propertyName, defaultValue: makeEnumProperty(true) },
        label,
        section,
        cases: [
            thisItemEnumPropertyDescriptorCase,
            new ColumnPropertyHandler(
                propertyName,
                label,
                [ColumnPropertyFlag.Editable, ColumnPropertyFlag.Required],
                undefined,
                undefined,
                (t, _d, s) => {
                    return t.columns.filter(c => {
                        if (!isSingleRelationType(c.type) || !isColumnNonHidden(c)) return false;
                        if (!forWriting) return true;
                        return s.findTable(c.type)?.isReadOnly !== true;
                    });
                },
                "table-ref",
                section
            ),
        ],
    };
}

export function getEditedColumnsForProperties(
    properties: readonly PropertyDescriptor[],
    desc: Description,
    tables: InputOutputTables,
    mutatingScreenKind: MutatingScreenKind | undefined,
    ccc: AppDescriptionContext,
    withActions: boolean
): EditedColumnsAndTables {
    let results: EditedColumnsAndTables = emptyEditedColumnsAndTables;
    for (const propertyDescr of properties) {
        const propertyAndDescriptor = getPropertyAndDescriptorFromDescription(desc, propertyDescr);
        if (propertyAndDescriptor === undefined) continue;

        const { description: pd, descriptorCase: pdc } = propertyAndDescriptor;

        const handler = handlerForPropertyKind(pdc.kind);
        results = combineEditedColumnsAndTables(
            results,
            handler.getEditedColumns(pdc, pd, desc, tables, mutatingScreenKind, ccc, withActions)
        );
    }
    return results;
}

export function makePlaceholderPropertyDescriptor(
    mutatingScreenKind: MutatingScreenKind | undefined,
    propertySection: SuperPropertySection,
    placeholderPlaceholder: string = "Enter placeholder"
): PropertyDescriptor {
    return makeTextPropertyDescriptor("placeholder", "Placeholder", placeholderPlaceholder, true, mutatingScreenKind, {
        searchable: false,
        propertySection,
    });
}

export function makeDefaultAction<T extends Description>(
    descriptor: ActionPropertyDescriptorCase,
    tables: InputOutputTables,
    desc: T
): ActionDescription | undefined {
    const { kinds, defaultAction } = descriptor;
    if (defaultAction === true) {
        assert(kinds.length > 0);
        return { kind: kinds[0] };
    } else if (typeof defaultAction === "function") {
        return defaultAction(tables.input, desc);
    }
    return undefined;
}

export function makeDynamicFilterColumnPropertyHandler(getIndirectTable: PropertyTableGetter | undefined) {
    return new ColumnPropertyHandler(
        "dynamicFilterColumn",
        "Filter by",
        [ColumnPropertyFlag.Editable, ColumnPropertyFlag.EmptyByDefault, ColumnPropertyFlag.Optional],
        getIndirectTable,
        ["category", "kind"],
        getPrimitiveOrPrimitiveArrayNonHiddenColumns,
        "string",
        PropertySection.InAppFilterData
    );
}

export const dynamicFilterColumnPropertyHandler = makeDynamicFilterColumnPropertyHandler(undefined);

export function makeScreenTitlePropertyDescriptor(
    mutatingScreenKind: MutatingScreenKind | undefined,
    placeholder: string = "Screen title"
): PropertyDescriptor {
    return makeTextPropertyDescriptor("title", "Title", placeholder, true, mutatingScreenKind, {
        preferredNames: titleProperties,
        propertySection: PropertySection.Design,
        searchable: false,
    });
}
